import React from "react";
import {
  Box, Container, Paper, Typography, Divider, Stack, Chip,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RestorePageOutlinedIcon from "@mui/icons-material/RestorePageOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import DownloadDoneOutlinedIcon from "@mui/icons-material/DownloadDoneOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";

import Sidebar from "../Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";
import ProcessFlow from "../ManualPage/ProcessFlow";
import LicenseRequestsQueue from "./LicenseRequestsQueue";

const FLOWS = [
  {
    title: "Validácia (kontrola)", color: "info", Icon: FactCheckOutlinedIcon,
    subtitle: "Iba skontroluje dokument, nič nemení.",
    steps: [
      { Icon: UploadFileOutlinedIcon, title: "Vloženie dokumentu", desc: "PDF / DOCX do modulu Validácia." },
      { Icon: TravelExploreOutlinedIcon, title: "Detekcia 3 rodín", desc: "GDPR + NIS2 + VYHRADENÉ." },
      { Icon: FactCheckOutlinedIcon, title: "Výsledok kontroly", desc: "Počty ukazovateľov po skupinách." },
      { Icon: CallSplitOutlinedIcon, title: "Rozhodnutie", desc: "Zdieľať / anonymizovať / pseudonymizovať." },
    ],
  },
  {
    title: "Anonymizácia", color: "warning", Icon: VisibilityOffOutlinedIcon,
    subtitle: "Nezvratné odstránenie údajov (GDPR čl. 5, 25, 32).",
    steps: [
      { Icon: UploadFileOutlinedIcon, title: "Vloženie súborov", desc: "PDF/DOCX/PNG/JPG, aj viac naraz." },
      { Icon: TuneOutlinedIcon, title: "Profil / detektory", desc: "GDPR/NIS2/VYHRADENÉ alebo jednorazovo." },
      { Icon: SearchOutlinedIcon, title: "Detekcia entít", desc: "Text aj OCR na obrázkoch." },
      { Icon: VisibilityOffOutlinedIcon, title: "Maskovanie", desc: "Nezvratné prekrytie." },
      { Icon: DownloadDoneOutlinedIcon, title: "Výstup + protokol", desc: "Predpona Anonymized; voliteľne do DB." },
    ],
  },
  {
    title: "Pseudonymizácia", color: "success", Icon: LockOutlinedIcon,
    subtitle: "Zvratné nahradenie pseudonymami (DOCX) s vaultom.",
    steps: [
      { Icon: UploadFileOutlinedIcon, title: "Vloženie .docx", desc: "Modul Pseudonymizácia." },
      { Icon: TuneOutlinedIcon, title: "Profil", desc: "Výber detektorov." },
      { Icon: SwapHorizOutlinedIcon, title: "Náhrada pseudonymami", desc: "Konzistentné zástupné hodnoty." },
      { Icon: LockOutlinedIcon, title: "Vault", desc: "Mapovanie + kľúč v dokumente." },
    ],
  },
  {
    title: "De-pseudonymizácia", color: "secondary", Icon: RestorePageOutlinedIcon,
    subtitle: "Obnova pôvodných údajov z vaultu.",
    steps: [
      { Icon: UploadFileOutlinedIcon, title: "Vloženie pseudo .docx", desc: "Modul De-pseudonymizácia." },
      { Icon: KeyOutlinedIcon, title: "Vault lookup", desc: "Kľúč vložený v dokumente." },
      { Icon: RestorePageOutlinedIcon, title: "Obnova originálu", desc: "Spätné nahradenie kódov." },
      { Icon: DownloadDoneOutlinedIcon, title: "Výstup", desc: "Pôvodný dokument." },
    ],
  },
  {
    title: "Onboarding a ponuka (vrátane A5)", color: "primary", Icon: HandshakeOutlinedIcon,
    subtitle: "Od leadu po vytvorenie prístupov.",
    steps: [
      { Icon: PersonAddAltOutlinedIcon, title: "Lead", desc: "Autocomplete RPO + FinStat (IČO/DIČ)." },
      { Icon: TuneOutlinedIcon, title: "Program / ponuka", desc: "A1–A4, alebo A5 individuálny (limit, kontá, prekročenie, platnosť)." },
      { Icon: DescriptionOutlinedIcon, title: "Zmluva / dodatok e-mailom", desc: "DOCX zmluva (A1–A4) / ponuka (A5)." },
      { Icon: DownloadDoneOutlinedIcon, title: "Akceptácia + prístupy", desc: "Provisioning vytvorí organizáciu + kontá." },
    ],
  },
  {
    title: "Zmena licenčného programu", color: "error", Icon: SwapHorizOutlinedIcon,
    subtitle: "Žiadosť → schválenie → dodatok → log.",
    steps: [
      { Icon: SwapHorizOutlinedIcon, title: "Žiadosť", desc: "Používateľ v O aplikácii požiada o zmenu (potvrdenie e-mailom)." },
      { Icon: RuleOutlinedIcon, title: "Fronta správcu", desc: "Superadmin schváli / zamietne (nižšie)." },
      { Icon: DescriptionOutlinedIcon, title: "Dodatok PDF", desc: "Dodatok č. N sa vygeneruje automaticky." },
      { Icon: MailOutlineIcon, title: "E-mail + log", desc: "Dodatok sa pošle a zapíše do histórie." },
    ],
  },
];

const Section = ({ title, children }) => (
  <Accordion disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, "&:before": { display: "none" }, mb: 1 }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails><Box sx={{ "& p": { mb: 1 }, color: "text.secondary", lineHeight: 1.7 }}>{children}</Box></AccordionDetails>
  </Accordion>
);

const SuperAdminManualPage = () => {
  const { isLoggedIn, user, isLoading } = useAuth();

  if (!isLoggedIn || isLoading) {
    return (<><Sidebar user={user} /><Container maxWidth="lg" sx={{ py: 4 }}><Typography>Načítavam…</Typography></Container></>);
  }

  return (
    <>
      <Sidebar user={user} />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: "1px solid", borderColor: "divider", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AdminPanelSettingsOutlinedIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Manuál správcu</Typography>
              <Typography variant="body2" color="text.secondary">Procesy a administrácia ISIT CENZOR pre superadministrátora.</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Funkčná fronta žiadostí o zmenu licencie */}
        <LicenseRequestsQueue />

        {/* Grafické znázornenie všetkých procesov */}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Procesy (grafické znázornenie)</Typography>
        <Stack spacing={2.5} sx={{ mb: 4 }}>
          {FLOWS.map((f) => (
            <ProcessFlow key={f.title} title={f.title} subtitle={f.subtitle} color={f.color} Icon={f.Icon} steps={f.steps} />
          ))}
        </Stack>

        <Divider sx={{ mb: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Administrácia – podrobnosti</Typography>

        <Section title="Onboarding leadov a ponuky">
          <p>V sekcii <b>Onboarding</b> vytvoríte lead (autocomplete podľa názvu firmy ťahá IČO/DIČ z RPO a FinStat). Pri leade vyberiete program a odošlete <b>návrh zmluvy</b> (A1–A4, DOCX). Pri <b>A5 (Individuálny plán)</b> zadáte počet súborov anonymizácie, počet užívateľských kont, cenu po prekročení za dokument a čas licencie do — uloží sa a odošle ponuka.</p>
          <p>Po akceptácii a vytvorení prístupov (provisioning) vznikne organizácia s príslušným programom; individuálne parametre A5 sa prenesú do organizácie.</p>
        </Section>

        <Section title="Schvaľovanie zmien licencie">
          <p>Používateľ požiada o zmenu programu v <b>O aplikácii</b>. Žiadosť sa objaví hore v <b>„Žiadosti o zmenu licencie"</b>. Po <b>Schváliť</b>: program organizácie sa prepne, vygeneruje sa <b>Dodatok č. N (PDF)</b>, pošle sa e-mailom kontaktnej osobe a zapíše do histórie. <b>Zamietnuť</b> ukončí žiadosť (s voliteľným dôvodom).</p>
          <p>Individuálny plán (A5) si používateľ sám neprepne — nastavuje ho správca cez onboarding/ponuku.</p>
        </Section>

        <Section title="Cenník a programy">
          <p><b>OFF PREMISE:</b> A1 (19 €), A2 (39 €), A3 (79 €), A4 (199 €) / mes. <b>ON PREMISE</b> (+ report): IN1 (49 €), IN2 (99 €), IN3 (dohodou). <b>A5 – Individuálny plán</b> (individuálna cena, parametre na mieru).</p>
        </Section>

        <Section title="Organizácie, používatelia, dáta">
          <p>Správa organizácií a kont je v superadmin sekciách (Onboarding, Organizácie). Štatistiky využívania a licencia každej organizácie sú v <b>O aplikácii</b> (panel Dáta) vrátane priebežného naplnenia programu a histórie zmien licencie.</p>
        </Section>

        <Box sx={{ mt: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip icon={<HistoryOutlinedIcon />} label="Manuál správcu ISIT CENZOR" size="small" variant="outlined" />
          <Chip label="www.cenzor.sk" size="small" variant="outlined" />
        </Box>
      </Container>
    </>
  );
};

export default SuperAdminManualPage;
